import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Button, Drawer, Grid, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import {
  SearchResultForCompany,
  SearchResultForCompanyUSDOT,
  SearchResultForFindSupport,
} from '../../../../../Utils/SearchResults';
import { supports } from '../../../../../API/TT_ELD/supports';
import SearchOptions from '../../../../../Utils/SearchOptions';
import { useDispatcher } from '../../../../../Hooks/TT_ELD/Supports';
import SupportUserTable from './SupportUserTable';
import { sendEvent } from '../../../../../Utils/analytics';
import { useTagQuerySync } from '../../../../../Utils/useTagQuerySync';
import useRouteQuery from '../../../../../Utils/useRouterQuery';

const { useBreakpoint } = Grid;

type numStr = number | string;

interface Fields {
  companyId: numStr;
  role: numStr;
  userId: numStr;
}

type SupportsInnerProps = {
  isMobile: boolean;
  companyName: string;
  setCompanyName: (v: string) => void;
  usdot: string;
  setUSDOt: (v: string) => void;
  dispatcherName: string;
  setDispatcherName: (v: string) => void;
  currentCompanyId: numStr | undefined;
  setCurrentCompanyId: (v: any) => void;
  dispatcherId: string | undefined;
  setDispatcherId: (v: string | undefined) => void;

  // react-final-form
  handleSubmit: () => void;

  // data
  data: any;
  isLoading: boolean | undefined;
  isFetching: any;
  refetch: () => void;
};

const SupportsInner = ({
  isMobile,
  companyName,
  setCompanyName,
  usdot,
  setUSDOt,
  dispatcherName,
  setDispatcherName,
  currentCompanyId,
  setCurrentCompanyId,
  dispatcherId,
  setDispatcherId,
  handleSubmit,
  data,
  isLoading,
  isFetching,
  refetch,
}: SupportsInnerProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const canSubmit = Boolean(currentCompanyId && dispatcherId);

  const handleSearchSelect =
    (setter: (val: any) => void, nameSetter?: (val: string) => void) =>
    (val: any, { valId }: { valId: string }) => {
      setter(valId);
      nameSetter?.(val);
    };

  const filtersUI = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'unset',
          gap: 10,
          width: '100%',
        }}
      >
        <Field
          name="companyId"
          render={() => (
            <SearchOptions
              SearchResult={SearchResultForCompany}
              value={companyName}
              onSelect={handleSearchSelect(setCurrentCompanyId, setCompanyName)}
              placeholder="Search by company"
              SelectWidth={isMobile ? '100%' : 400}
            />
          )}
        />

        <Field
          name="companyId"
          render={() => (
            <SearchOptions
              SearchResult={SearchResultForCompanyUSDOT}
              value={usdot}
              onSelect={handleSearchSelect(setCurrentCompanyId, setUSDOt)}
              placeholder="Search by USDOT"
              SelectWidth={isMobile ? '100%' : 400}
            />
          )}
        />

        <Field
          name="userId"
          render={() => (
            <SearchOptions
              SearchResult={SearchResultForFindSupport}
              value={dispatcherName}
              onSelect={handleSearchSelect(setDispatcherId, setDispatcherName)}
              placeholder="Support user"
              SelectWidth={isMobile ? '100%' : 320}
            />
          )}
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              disabled={!canSubmit}
              onClick={() => {
                handleSubmit();
                setFiltersOpen(false);
              }}
            >
              Apply & Submit
            </Button>
          </Space>
        )}
      </div>
    ),
    [companyName, usdot, dispatcherName, isMobile, canSubmit],
  );

  return (
    <>
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          {/* Desktop inline filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>{filtersUI}</div>

          {/* Actions right */}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button type="primary" size="large" disabled={!canSubmit} onClick={handleSubmit}>
              Submit
            </Button>
            <Button size="large" onClick={refetch}>
              Refresh
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />
          <Button block size="large" onClick={refetch}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      <SupportUserTable
        company={data}
        dispatcherId={dispatcherId}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
      />
    </>
  );
};

const Supports = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [companyName, setCompanyName] = useRouteQuery('', 'companyName', String);
  const [currentCompanyId, setCurrentCompanyId] = useState<undefined | number | string>(undefined);
  const [usdot, setUSDOt] = useRouteQuery('', 'usdot', String);
  const [dispatcherName, setDispatcherName] = useRouteQuery('', 'dispatcherName', String);
  const [dispatcherId, setDispatcherId] = useState<string | undefined>('');

  useTagQuerySync(
    { en_US: 'Supports', zh_CN: '' },
    'supports',
    { companyName, currentCompanyId, usdot, dispatcherName, dispatcherId },
    {
      companyName: setCompanyName,
      currentCompanyId: setCurrentCompanyId,
      usdot: setUSDOt,
      dispatcherName: setDispatcherName,
      dispatcherId: setDispatcherId,
    },
  );

  const { data, isLoading, refetch, isFetching } = useDispatcher(dispatcherId);

  const onSubmit = async (values: Fields) => {
    if (currentCompanyId && dispatcherId) {
      values.role = 'support-user';
      values.companyId = currentCompanyId;
      values.userId = dispatcherId;

      await supports.supportPatchData(values);
      sendEvent(`Support Add Company Submit`, `Add Company in Support page`, `companyId`, '1');
      refetch();
      SearchResultForCompany('');
    }
  };

  return (
    <div className="mainPage">
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <SupportsInner
              isMobile={isMobile}
              companyName={companyName}
              setCompanyName={setCompanyName}
              usdot={usdot}
              setUSDOt={setUSDOt}
              dispatcherName={dispatcherName}
              setDispatcherName={setDispatcherName}
              currentCompanyId={currentCompanyId}
              setCurrentCompanyId={setCurrentCompanyId}
              dispatcherId={dispatcherId}
              setDispatcherId={setDispatcherId}
              handleSubmit={handleSubmit}
              data={data}
              isLoading={isLoading}
              isFetching={isFetching}
              refetch={refetch}
            />
          </form>
        )}
      />
    </div>
  );
};

export default Supports;
